import React, { useEffect, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Fingerprint, Trash2, Plus, Smartphone, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import { useWebAuthn, isWebAuthnSupported } from '../lib/webauthn';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ManagePasskeysCard
 * Lives inside the Profile / Settings page.
 *
 * Lets the user:
 *  - See every passkey they've enrolled (label, created date, last used)
 *  - Add a NEW passkey for the current device (so they can register
 *    multiple devices — iPhone + iPad + Mac, etc.)
 *  - Remove a passkey (for a lost / stolen device)
 *
 * Backend endpoints used:
 *   GET    /api/webauthn/credentials
 *   POST   /api/webauthn/register-begin + /register-complete  (via useWebAuthn)
 *   DELETE /api/webauthn/credentials/{credential_id}
 */
function ManagePasskeysCard() {
  const supported = isWebAuthnSupported();
  const { busy: enrolling, enrolPasskey } = useWebAuthn();

  const [loading, setLoading] = useState(true);
  const [passkeys, setPasskeys] = useState([]);
  const [labelInput, setLabelInput] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/api/webauthn/credentials`);
      if (!res.ok) {
        setPasskeys([]);
        return;
      }
      const data = await res.json();
      setPasskeys(Array.isArray(data.passkeys) ? data.passkeys : []);
    } catch (e) {
      setPasskeys([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (supported) refresh();
    else setLoading(false);
  }, [refresh, supported]);

  const handleAdd = async () => {
    try {
      const label = labelInput.trim() || guessDeviceLabel();
      await enrolPasskey({ label });
      toast.success(`'${label}' added: you can now sign in with this device.`);
      setLabelInput('');
      setShowAddForm(false);
      await refresh();
    } catch (e) {
      toast.error(e.message || 'Could not add passkey');
    }
  };

  const handleRemove = async (credentialId, label) => {
    const confirmed = window.confirm(
      `Remove "${label}"?\n\nThis device will no longer be able to sign in with Face ID / Fingerprint. You can always add it back later.`
    );
    if (!confirmed) return;
    setRemovingId(credentialId);
    try {
      const res = await authFetch(
        `${API}/api/webauthn/credentials/${encodeURIComponent(credentialId)}`,
        { method: 'DELETE' }
      );
      if (!res.ok) throw new Error('Could not remove passkey');
      toast.success(`Removed '${label}'`);
      await refresh();
    } catch (e) {
      toast.error(e.message || 'Could not remove passkey');
    } finally {
      setRemovingId(null);
    }
  };

  if (!supported) {
    return (
      <div
        className="bg-white border border-zinc-200 rounded-md p-5"
        data-testid="manage-passkeys-card"
      >
        <div className="flex items-center gap-2 mb-2">
          <Fingerprint className="w-4 h-4 text-zinc-500" />
          <h3 className="text-sm font-bold text-zinc-950 uppercase tracking-wider">
            Passkeys
          </h3>
        </div>
        <p className="text-xs text-zinc-600 leading-relaxed">
          Your current browser doesn't support Face ID / Fingerprint sign-in. Try a recent
          version of Safari, Chrome, Firefox or Edge on a device with biometrics.
        </p>
      </div>
    );
  }

  return (
    <div
      className="bg-white border border-zinc-200 rounded-md p-5"
      data-testid="manage-passkeys-card"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <Fingerprint className="w-4 h-4 text-zinc-700" />
            <h3 className="text-sm font-bold text-zinc-950 uppercase tracking-wider">
              Passkeys
            </h3>
          </div>
          <p className="text-xs text-zinc-600 leading-relaxed mt-1">
            Sign in with Face ID, Touch ID or your device PIN. You can register multiple
            devices: phone, tablet and laptop: and any of them will work.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-zinc-400">
          <Loader2 className="w-4 h-4 animate-spin" />
        </div>
      ) : passkeys.length === 0 ? (
        <div
          className="flex items-start gap-3 bg-zinc-50 border border-zinc-200 rounded-md p-3 text-xs text-zinc-700 leading-relaxed mb-3"
          data-testid="passkeys-empty-state"
        >
          <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0 text-zinc-500" />
          <p>
            No passkeys yet. Add one for this device below: your password will keep
            working as a backup.
          </p>
        </div>
      ) : (
        <ul className="space-y-2 mb-3" data-testid="passkeys-list">
          {passkeys.map((p) => (
            <li
              key={p.credential_id}
              className="flex items-center justify-between gap-3 px-3 py-2 bg-zinc-50 border border-zinc-200 rounded-md"
              data-testid="passkey-item"
            >
              <div className="flex items-center gap-3 min-w-0">
                <Smartphone className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-zinc-950 truncate">
                    {p.label}
                  </p>
                  <p className="text-[11px] text-zinc-500 leading-tight">
                    {formatRelative(p.last_used_at)
                      ? `Last used ${formatRelative(p.last_used_at)}`
                      : `Added ${formatRelative(p.created_at) || 'recently'}`}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRemove(p.credential_id, p.label)}
                disabled={removingId === p.credential_id}
                className="text-zinc-400 hover:text-red-600 p-1 flex-shrink-0 disabled:opacity-50"
                aria-label={`Remove ${p.label}`}
                data-testid={`passkey-remove-btn-${p.credential_id.slice(0, 12)}`}
              >
                {removingId === p.credential_id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {showAddForm ? (
        <div className="space-y-2 border-t border-zinc-100 pt-3">
          <label className="block text-xs font-bold text-zinc-700 uppercase tracking-wider">
            Device name (optional)
          </label>
          <input
            type="text"
            value={labelInput}
            onChange={(e) => setLabelInput(e.target.value)}
            placeholder={guessDeviceLabel()}
            maxLength={60}
            className="w-full h-10 px-3 text-sm bg-white border border-zinc-300 focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950 rounded-md"
            data-testid="passkey-label-input"
          />
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={handleAdd}
              disabled={enrolling}
              className="flex-1 bg-zinc-950 text-white hover:bg-zinc-800 h-10"
              data-testid="passkey-add-confirm-btn"
            >
              <Fingerprint className="w-4 h-4 mr-2" />
              {enrolling ? 'Setting up…' : 'Add this device'}
            </Button>
            <Button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                setLabelInput('');
              }}
              variant="outline"
              className="h-10"
              disabled={enrolling}
              data-testid="passkey-add-cancel-btn"
            >
              Cancel
            </Button>
          </div>
          <p className="text-[11px] text-zinc-500 leading-relaxed">
            Already on another device? Sign in there with your password and come back to
            this card to add it.
          </p>
        </div>
      ) : (
        <Button
          type="button"
          onClick={() => setShowAddForm(true)}
          variant="outline"
          className="w-full h-11"
          data-testid="passkey-add-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          {passkeys.length === 0 ? 'Add a passkey' : 'Add another device'}
        </Button>
      )}
    </div>
  );
}

function guessDeviceLabel() {
  const ua = navigator.userAgent || '';
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/iPad/i.test(ua)) return 'iPad';
  if (/Mac OS X|Macintosh/i.test(ua)) return 'Mac';
  if (/Android/i.test(ua)) return 'Android device';
  if (/Windows/i.test(ua)) return 'Windows device';
  if (/Linux/i.test(ua)) return 'Linux device';
  return 'This device';
}

function formatRelative(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffSec = Math.max(1, Math.round((Date.now() - then) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  const diffMonth = Math.round(diffDay / 30);
  if (diffMonth < 12) return `${diffMonth} month${diffMonth === 1 ? '' : 's'} ago`;
  const diffYr = Math.round(diffMonth / 12);
  return `${diffYr} year${diffYr === 1 ? '' : 's'} ago`;
}

export default ManagePasskeysCard;
